import * as React from "react";
import { Input } from "../atoms/input.js";
import { Slider } from "../atoms/slider.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";
import { ToggleGroup, ToggleGroupItem } from "./toggle-group.js";

/**
 * Employer-contribution controls — the benefits-domain molecule shared by the
 * quote step and the owner contribution editor. Two shapes:
 *
 *   - `PercentControl` — a single slider + % readout, clamped to a minimum
 *     (medical-style, where a % floor applies).
 *   - `ContributionControl` — a %-or-$ toggle: `percent` shows the slider,
 *     `fixed` shows a per-employee dollar input (ancillary-style).
 *
 * Composed from DS `Slider`, `Input`, `Typography`, and `ToggleGroup` — no
 * hand-rolled toggle, no app-level utility classes.
 */

export type ContributionMode = "percent" | "fixed";

export interface ProductContribution {
  mode: ContributionMode;
  /** Employer share as a percent of the product premium. */
  percent: number;
  /** Employer fixed dollars per employee, per month. */
  fixed: number;
}

/** Default fixed employer contribution ($/employee/mo) on first switch to $. */
export const CONTRIBUTION_DEFAULT_FIXED = 10;

export interface PercentControlProps {
  value: number;
  /** Lower clamp for the slider (e.g. a required % floor). */
  min?: number;
  step?: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  "aria-label"?: string;
}

/** Slider + % readout, clamped to a minimum. */
export const PercentControl = React.forwardRef<HTMLDivElement, PercentControlProps>(
  function PercentControl(
    { value, min = 0, step = 5, disabled, onChange, "aria-label": ariaLabel },
    ref,
  ) {
    return (
      <div ref={ref} className="cw-v2-contribution">
        <Slider
          className="cw-v2-contribution__slider"
          value={[value]}
          min={min}
          max={100}
          step={step}
          disabled={disabled}
          onValueChange={([v]) => onChange(v ?? min)}
          aria-label={ariaLabel ?? "Employer contribution percent"}
        />
        <Typography
          as="span"
          variant="body"
          className={cx(
            "cw-v2-contribution__readout",
            disabled && "cw-v2-contribution__readout--disabled",
          )}
        >
          {value}%
        </Typography>
      </div>
    );
  },
);
PercentControl.displayName = "PercentControl";

export interface ContributionControlProps {
  contribution: ProductContribution;
  disabled?: boolean;
  step?: number;
  onChange: (next: ProductContribution) => void;
}

/** %-or-$ toggle: `percent` → slider, `fixed` → per-employee dollar input. */
export const ContributionControl = React.forwardRef<HTMLDivElement, ContributionControlProps>(
  function ContributionControl({ contribution, disabled, step = 5, onChange }, ref) {
    return (
      <div ref={ref} className="cw-v2-contribution cw-v2-contribution--with-toggle">
        <ToggleGroup
          type="single"
          size="sm"
          value={contribution.mode}
          disabled={disabled}
          onValueChange={(mode) => {
            if (!mode) return; // ignore deselect
            const next = mode as ContributionMode;
            onChange({
              ...contribution,
              mode: next,
              // Default fixed contribution on first switch to $.
              fixed:
                next === "fixed" && contribution.fixed === 0
                  ? CONTRIBUTION_DEFAULT_FIXED
                  : contribution.fixed,
            });
          }}
          aria-label="Employer contribution mode"
        >
          <ToggleGroupItem value="percent" aria-label="Percent of premium">
            %
          </ToggleGroupItem>
          <ToggleGroupItem value="fixed" aria-label="Fixed dollars per employee">
            $
          </ToggleGroupItem>
        </ToggleGroup>

        {contribution.mode === "percent" ? (
          <div className="cw-v2-contribution">
            <Slider
              className="cw-v2-contribution__slider"
              value={[contribution.percent]}
              min={0}
              max={100}
              step={step}
              disabled={disabled}
              onValueChange={([v]) =>
                onChange({ ...contribution, percent: v ?? contribution.percent })
              }
              aria-label="Employer contribution percent"
            />
            <Typography
              as="span"
              variant="body"
              className={cx(
                "cw-v2-contribution__readout",
                disabled && "cw-v2-contribution__readout--disabled",
              )}
            >
              {contribution.percent}%
            </Typography>
          </div>
        ) : (
          <div
            className={cx(
              "cw-v2-contribution__fixed",
              disabled && "cw-v2-contribution__fixed--disabled",
            )}
          >
            <Typography as="span" variant="body" muted>
              $
            </Typography>
            <Input
              type="number"
              size="sm"
              min={0}
              disabled={disabled}
              value={contribution.fixed === 0 ? "" : String(contribution.fixed)}
              onChange={(e) =>
                onChange({ ...contribution, fixed: Math.max(0, Number(e.target.value) || 0) })
              }
              className="cw-v2-contribution__fixed-input"
              aria-label="Employer fixed contribution per employee"
            />
            <Typography as="span" variant="body" muted className="cw-v2-contribution__fixed-suffix">
              / employee
            </Typography>
          </div>
        )}
      </div>
    );
  },
);
ContributionControl.displayName = "ContributionControl";
